import React, {useMemo, useState, useEffect} from 'react';
import {ChevronDown, ChevronUp} from 'lucide-react';
import {PatientDashboardSliceInitalState} from '@/interface';
import {Checkbox, Button, Icon, Modal} from '@/ui-kits';
import {useSelector} from 'react-redux';
import {recentCasesSelector, patientDashboardExistingFileIds} from '@/services/redux/selectors/patientDashboardSelector';
import { FileViewerModal } from '../../../common';
import { cn } from '@/utils/common_functions';

type RecentCase = PatientDashboardSliceInitalState['recentCases'][number];
export type RecentCaseFile = RecentCase['files'][number];


interface RecentCasesModalProps {
  open: boolean;
  onClose: () => void;
  onAddSelectedDocuments: (files: RecentCaseFile[]) => void;
  mode?: 'select' | 'reuse';
  specialistName?: string;
  specialization?: string;
  reuseCases?: { id: number; close_at: string; files: { id: number; name: string; url: string }[] }[];
  alreadyAddedFileIds?: number[];
}

const formatClosedAt = (value?: string | null): string => {
  if (!value) return '--';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value));
};

export const RecentCasesModal: React.FC<RecentCasesModalProps> = ({open, onClose, onAddSelectedDocuments, mode = 'select', specialization, reuseCases, alreadyAddedFileIds = []}) => {
  const recentCases = useSelector(recentCasesSelector) as RecentCase[];
  const existingFileIds = useSelector(patientDashboardExistingFileIds);
  const [openCaseIds, setOpenCaseIds] = useState<number[]>([]);
  const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
  const [filePreviewOpen, setFilePreviewOpen] = useState(false);
  const [fileToPreview, setFileToPreview] = useState<RecentCaseFile | null>(null);
  const [prevMode, setPrevMode] = useState<'select' | 'reuse'>(mode);

  // Initialize selectedFileIds from Redux when modal opens
  useEffect(() => {
    setSelectedFileIds(existingFileIds || []);
  }, [existingFileIds]);
  
  useEffect(() => {
    if (mode !== prevMode) {
      setSelectedFileIds([]);
      setPrevMode(mode);
    }
  }, [mode, prevMode]);

  const reuseAllFiles = useMemo(() => (reuseCases || []).flatMap(c => c.files), [reuseCases]);
  const cases = useMemo(() => recentCases.slice(0, 3), [recentCases]);

  const selectedFiles = useMemo(() => {
    if (mode === 'reuse') return reuseAllFiles.filter(f => selectedFileIds.includes(f.id));
    return cases.flatMap(recentCase => (recentCase.files || []).filter(file => selectedFileIds.includes(file.id)));
  }, [mode, cases, reuseAllFiles, selectedFileIds]);

  const toggleCase = (caseId: number) => {
    setOpenCaseIds(prev => (prev.includes(caseId) ? prev.filter(id => id !== caseId) : [...prev, caseId]));
  };

  const toggleFileSelection = (fileId: number, checked: boolean) => {
    if (checked && alreadyAddedFileIds.includes(fileId)) {
      return;
    }
    setSelectedFileIds(prev => {
      if (checked) return [...prev, fileId];
      return prev.filter(id => id !== fileId);
    });
  };

  const toggleSelectAll = (files: RecentCase['files'], checked: boolean) => {
    const ids = (files || [])
      .map(f => f.id)
      .filter(id => !alreadyAddedFileIds.includes(id));
    setSelectedFileIds(prev =>
      checked ? [...new Set([...prev, ...ids])] : prev.filter(id => !ids.includes(id)),
    );
  };

  const handleClose = () => {
    setOpenCaseIds([]);
    setFilePreviewOpen(false);
    setFileToPreview(null);
    onClose();
  };

  const handleAddSelectedDocuments = () => {
    onAddSelectedDocuments(selectedFiles);
    handleClose();
  };

  const handleViewFile = (file: RecentCaseFile) => {
    setFileToPreview(file);
    setFilePreviewOpen(true);
  };

  return (
    <Modal open={open} onClose={handleClose} className="max-w-[750px] rounded-[18px] p-1 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-ui_input_border/60"
        style={{background: 'linear-gradient(180deg, #FCFEFF 0%, #E9F6FC 100%)'}}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-FigtreeBold text-ui_blue">
              {mode === 'reuse' ? 'Reuse Consultation Files' : 'Select Previous Files'}
            </h2>
            <p className="mt-0.5 text-xs font-FigtreeRegular text-ui_input_border_dark">
              {mode === 'reuse' ? `Select files from your previous ${specialization ?? ''} consultation` : 'Select files from your previous consultation'}
            </p>
          </div>
          <button onClick={handleClose} className="p-1">
            <Icon type="close" className="size-6 text-ui_blue" />
          </button>
        </div>
      </div>

      {/* Cases List */}
      <div className="max-h-[65vh] overflow-y-auto px-4 py-4 sm:px-6 sm:py-6 bg-white">
        {mode === 'reuse' ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 mb-1">
              <Checkbox
                gradient
                checked={reuseAllFiles.length > 0 && reuseAllFiles.filter(f => !alreadyAddedFileIds.includes(f.id)).every(f => selectedFileIds.includes(f.id))}
                onChange={checked => setSelectedFileIds(checked ? reuseAllFiles.filter(f => !alreadyAddedFileIds.includes(f.id)).map(f => f.id) : [])}
              />
              <span className="text-xs font-FigtreeSemiBold text-ui_input_border_dark">
                Select all ({reuseAllFiles.filter(f => !alreadyAddedFileIds.includes(f.id)).length} files)
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {reuseAllFiles.map(file => {
                const isAlreadyAdded = alreadyAddedFileIds.includes(file.id);
                return (
                  <div key={file.id} className={cn(
                    "flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 transition-all duration-150",
                    isAlreadyAdded
                      ? "border-ui_input_border/40 bg-gray-100 opacity-50"
                      : selectedFileIds.includes(file.id)
                      ? "border-ui_blue bg-ui_sky_blue"
                      : "border-ui_input_border/60 bg-[#F9FCFE]"
                  )}>
                    <div className="flex min-w-0 items-center gap-2">
                      <Checkbox
                        gradient
                        checked={selectedFileIds.includes(file.id)}
                        onChange={checked => toggleFileSelection(file.id, checked)}
                        disabled={isAlreadyAdded}
                      />
                      <p className={cn("truncate text-xs font-FigtreeSemiBold", isAlreadyAdded ? "text-gray-400" : "text-ui_blue")}>
                        {file.name}
                        {isAlreadyAdded && " (Added)"}
                      </p>
                    </div>
                    <Button
                      title="View"
                      variant="secondary"
                      size="sm"
                      className="shrink-0 px-3 py-1 rounded-lg"
                      onClick={() => handleViewFile(file)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
          {cases.map(recentCase => {
            const isOpen = openCaseIds.includes(recentCase.id);
            const allSelected = (recentCase.files || []).length > 0 && (recentCase.files || []).every(f => selectedFileIds.includes(f.id));

            return (
              <div key={recentCase.id} className="rounded-2xl border border-ui_input_border/40 overflow-hidden bg-white shadow-[0_0_8px_rgba(0,0,0,0.06)]">
                {/* Case Row */}
                <button
                  type="button"
                  onClick={() => toggleCase(recentCase.id)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-5 text-left">
                  <div className="flex items-center gap-3 flex-1">
                    {/* Icon */}
                    <div className="w-11 h-11 rounded-full bg-[#CBEBFB] flex items-center justify-center flex-shrink-0">
                      <Icon type="previous_case_file" className="size-6 text-ui_blue" />
                    </div>
                    {/* Name + Specialization · Date */}
                    <div>
                      <p className="text-base font-FigtreeBold text-ui_blue">{recentCase.doctor_name}</p>
                      <p className="text-sm font-FigtreeRegular text-ui_input_border_dark">
                        {recentCase.specialization?.name ?? '--'} · {formatClosedAt(recentCase.close_at)}
                      </p>
                    </div>
                  </div>
                  {isOpen ? <ChevronUp className="size-5 text-ui_blue" /> : <ChevronDown className="size-5 text-ui_blue" />}
                </button>

                {/* Expanded Files */}
                {isOpen && (
                  <div className="border-t border-ui_input_border/60 bg-white px-4 py-4">
                    {/* Select All */}
                    <div className="flex items-center gap-2 mb-3">
                      <Checkbox
                        checked={allSelected}
                        onChange={checked => toggleSelectAll(recentCase.files, checked)}
                        inputClassName="size-4"
                        gradient
                      />
                      <span className="text-xs font-FigtreeSemiBold text-ui_input_border_dark">
                        Select all ({(recentCase.files || []).filter(f => !alreadyAddedFileIds.includes(f.id)).length} files)
                      </span>
                    </div>

                    {/* Files Grid */}
                    <div className="grid grid-cols-2 gap-3">
                      {(recentCase.files || []).map(file => {
                        const isAlreadyAdded = alreadyAddedFileIds.includes(file.id);
                        return (
                          <div key={file.id} className={cn(
                              "flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 transition-all duration-150",
                              isAlreadyAdded
                                ? "border-ui_input_border/40 bg-gray-100 opacity-50"
                                : selectedFileIds.includes(file.id)
                                ? "border-ui_blue bg-ui_sky_blue"
                                : "border-ui_input_border/60 bg-[#F9FCFE]"
                            )}>
                            <div className="flex min-w-0 items-center gap-2">
                              <Checkbox
                                checked={selectedFileIds.includes(file.id)}
                                onChange={checked => toggleFileSelection(file.id, checked)}
                                inputClassName="size-4"
                                gradient
                                disabled={isAlreadyAdded}
                              />
                              <p className={cn("truncate text-xs font-FigtreeSemiBold", isAlreadyAdded ? "text-gray-400" : "text-ui_blue")}>
                                {file.name}
                                {isAlreadyAdded && " (Added)"}
                              </p>
                            </div>
                            <Button
                              title="View"
                              variant="secondary"
                              size="sm"
                              className="shrink-0 px-3 py-1 rounded-lg"
                              onClick={() => handleViewFile(file)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex flex-col gap-4 border-t border-ui_input_border/60 bg-white px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm font-FigtreeSemiBold text-ui_blue">Selected ({selectedFiles.length})</p>
        <div className="flex gap-3 self-end sm:self-auto">
          <Button
            title="Cancel"
            variant="secondary"
            size="md"
            className="rounded-lg px-8 py-3"
            textClassName="font-FigtreeBold gradient-text"
            onClick={handleClose}
          />
          <Button
            title="Add Selected Documents"
            variant="primary"
            size="md"
            className="rounded-lg px-8 py-3"
            onClick={handleAddSelectedDocuments}
            disabled={selectedFiles.length === 0}
          />
        </div>
      </div>

      {/* File Viewer Modal */}
      {fileToPreview && (
        <FileViewerModal
          isOpen={filePreviewOpen}
          onClose={() => setFilePreviewOpen(false)}
          file={{ id: fileToPreview.id, name: fileToPreview.name, url: fileToPreview.url }}
          showDownloadButton={true}
          isSecure={true}
        />
      )}
    </Modal>
  );
};

export default RecentCasesModal;